import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './FarmDiary.css';

const PERIOD_OPTIONS = [
  { value: '1', label: '오늘' },
  { value: '7', label: '7일' },
  { value: '30', label: '30일' },
];

const METRIC_META = {
  temperature: { title: '온도', unit: '℃', icon: '🌡️' },
  humidity: { title: '습도', unit: '%', icon: '💧' },
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatValue = (value, unit = '') => {
  const number = toNumber(value);
  if (number === null) return '-';
  return `${number.toFixed(1)}${unit}`;
};

const formatTime = (value, periodDays) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('ko-KR', {
    month: periodDays > 1 ? '2-digit' : undefined,
    day: periodDays > 1 ? '2-digit' : undefined,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const getStatus = (current, min, max) => {
  const value = toNumber(current);
  const minimum = toNumber(min);
  const maximum = toNumber(max);

  if (value === null || minimum === null || maximum === null) {
    return { text: '기준 미설정', className: 'text-bg-secondary' };
  }

  if (value < minimum) {
    return { text: '낮음', className: 'text-bg-warning' };
  }

  if (value > maximum) {
    return { text: '높음', className: 'text-bg-danger' };
  }

  return { text: '정상', className: 'text-bg-success' };
};

const LineChart = ({ points, unit, periodDays }) => {
  const values = (Array.isArray(points) ? points : [])
    .map((point) => ({
      ...point,
      numericValue: toNumber(point.value),
    }))
    .filter((point) => point.numericValue !== null);

  if (values.length < 2) {
    return (
      <div className="py-5 text-center text-secondary">
        표시할 이력 데이터가 충분하지 않습니다.
      </div>
    );
  }

  const width = 900;
  const height = 260;
  const paddingLeft = 48;
  const paddingRight = 18;
  const paddingTop = 18;
  const paddingBottom = 42;

  const minValue = Math.min(...values.map((item) => item.numericValue));
  const maxValue = Math.max(...values.map((item) => item.numericValue));
  const valueRange = Math.max(maxValue - minValue, 1);

  const plotWidth = width - paddingLeft - paddingRight;
  const plotHeight = height - paddingTop - paddingBottom;

  const polylinePoints = values
    .map((item, index) => {
      const x =
        paddingLeft +
        (index / Math.max(values.length - 1, 1)) * plotWidth;
      const y =
        paddingTop +
        ((maxValue - item.numericValue) / valueRange) * plotHeight;
      return `${x},${y}`;
    })
    .join(' ');

  const first = values[0];
  const middle = values[Math.floor(values.length / 2)];
  const last = values[values.length - 1];

  return (
    <div className="w-100 overflow-hidden">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={`센서값 변화 그래프, 최저 ${minValue.toFixed(
          1
        )}${unit}, 최고 ${maxValue.toFixed(1)}${unit}`}
        style={{ width: '100%', minHeight: 220 }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = paddingTop + ratio * plotHeight;
          const label = maxValue - ratio * valueRange;

          return (
            <g key={ratio}>
              <line
                x1={paddingLeft}
                y1={y}
                x2={width - paddingRight}
                y2={y}
                stroke="#e9ecef"
                strokeWidth="1"
              />
              <text
                x={paddingLeft - 8}
                y={y + 4}
                textAnchor="end"
                fontSize="12"
                fill="#6c757d"
              >
                {label.toFixed(1)}
              </text>
            </g>
          );
        })}

        <polyline
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={polylinePoints}
          className="text-primary"
        />

        {[first, middle, last].map((item, index) => {
          const itemIndex = values.indexOf(item);
          const x =
            paddingLeft +
            (itemIndex / Math.max(values.length - 1, 1)) * plotWidth;

          return (
            <text
              key={`${item.time}-${index}`}
              x={x}
              y={height - 12}
              textAnchor={
                index === 0 ? 'start' : index === 2 ? 'end' : 'middle'
              }
              fontSize="12"
              fill="#6c757d"
            >
              {formatTime(item.time, periodDays)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

const MetricCard = ({ metricKey, metric, settings, periodDays }) => {
  const meta = METRIC_META[metricKey];
  const minimum = settings?.[`${metricKey}_min`];
  const maximum = settings?.[`${metricKey}_max`];
  const status = getStatus(metric?.current, minimum, maximum);

  return (
    <section className="card border-0 shadow-sm mb-4">
      <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-3 py-3">
        <div>
          <div className="d-flex align-items-center gap-2">
            <span className="fs-4" aria-hidden="true">
              {meta.icon}
            </span>
            <strong>{meta.title}</strong>
          </div>
          <div className="text-secondary small mt-1">
            {metric?.entity_name || metric?.entity_id || '센서 미등록'}
          </div>
        </div>

        <div className="text-end">
          <div className="d-flex align-items-center gap-2 justify-content-end">
            <strong className="fs-3">
              {formatValue(metric?.current, meta.unit)}
            </strong>
            <span className={`badge ${status.className}`}>
              {status.text}
            </span>
          </div>
          <div className="small text-secondary mt-1">
            정상 기준 {formatValue(minimum, meta.unit)} ~{' '}
            {formatValue(maximum, meta.unit)}
          </div>
        </div>
      </div>

      <div className="card-body p-3 p-md-4">
        <div className="row g-3 mb-3">
          <div className="col-4">
            <div className="bg-light rounded p-3 text-center h-100">
              <div className="small text-secondary mb-1">평균</div>
              <div className="fw-bold">
                {formatValue(metric?.average, meta.unit)}
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="bg-light rounded p-3 text-center h-100">
              <div className="small text-secondary mb-1">최저</div>
              <div className="fw-bold">
                {formatValue(metric?.minimum, meta.unit)}
              </div>
            </div>
          </div>
          <div className="col-4">
            <div className="bg-light rounded p-3 text-center h-100">
              <div className="small text-secondary mb-1">최고</div>
              <div className="fw-bold">
                {formatValue(metric?.maximum, meta.unit)}
              </div>
            </div>
          </div>
        </div>

        <LineChart
          points={metric?.points || []}
          unit={meta.unit}
          periodDays={periodDays}
        />
      </div>
    </section>
  );
};

const MemberSmartfarmAnalysis = () => {
  const navigate = useNavigate();
  const api = useMemo(
    () =>
      axios.create({
        withCredentials: true,
      }),
    []
  );

  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [period, setPeriod] = useState('1');
  const [analysis, setAnalysis] = useState(null);
  const [settings, setSettings] = useState({
    temperature_min: '',
    temperature_max: '',
    humidity_min: '',
    humidity_max: '',
  });

  const [pageLoading, setPageLoading] = useState(true);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleError = useCallback(
    (err, fallback) => {
      const status = err?.response?.status;
      const redirect = err?.response?.data?.redirect;
      const message =
        err?.response?.data?.error || err?.message || fallback;

      if (status === 401) {
        navigate(redirect || '/login', { replace: true });
        return;
      }

      setError(message);
    },
    [navigate]
  );

  const loadSites = useCallback(async () => {
    const response = await api.get('/api/member/smartfarm/sites');
    const data = response.data || {};

    if (!data.ok) {
      throw new Error(data.error || '농장 목록을 불러오지 못했습니다.');
    }

    const nextSites = Array.isArray(data.sites) ? data.sites : [];
    setSites(nextSites);
    setSelectedSiteId((previous) => {
      if (
        previous &&
        nextSites.some((site) => String(site.id) === String(previous))
      ) {
        return previous;
      }
      return nextSites[0]?.id ? String(nextSites[0].id) : '';
    });

    return nextSites;
  }, [api]);

  const loadAnalysis = useCallback(
    async (siteId, nextPeriod = period) => {
      if (!siteId) {
        setAnalysis(null);
        return;
      }

      setAnalysisLoading(true);
      setError('');

      try {
        const response = await api.get(
          '/api/member/smartfarm/environment-analysis',
          {
            params: {
              site_id: Number(siteId),
              days: Number(nextPeriod),
            },
          }
        );

        const data = response.data || {};
        if (!data.ok) {
          throw new Error(data.error || '환경분석을 불러오지 못했습니다.');
        }

        setAnalysis(data);
        setSettings({
          temperature_min: data.settings?.temperature_min ?? '',
          temperature_max: data.settings?.temperature_max ?? '',
          humidity_min: data.settings?.humidity_min ?? '',
          humidity_max: data.settings?.humidity_max ?? '',
        });
      } catch (err) {
        handleError(err, '환경분석을 불러오지 못했습니다.');
      } finally {
        setAnalysisLoading(false);
      }
    },
    [api, handleError, period]
  );

  useEffect(() => {
    const initialize = async () => {
      setPageLoading(true);
      setError('');

      try {
        const nextSites = await loadSites();
        if (nextSites.length > 0) {
          await loadAnalysis(String(nextSites[0].id), period);
        }
      } catch (err) {
        handleError(err, '환경분석 화면을 준비하지 못했습니다.');
      } finally {
        setPageLoading(false);
      }
    };

    initialize();
  }, [handleError, loadAnalysis, loadSites, period]);

  useEffect(() => {
    if (!pageLoading && selectedSiteId) {
      loadAnalysis(selectedSiteId, period);
    }
  }, [loadAnalysis, pageLoading, period, selectedSiteId]);

  const handleSettingChange = (event) => {
    const { name, value } = event.target;
    setSettings((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    if (!selectedSiteId || settingsSaving) return;

    setSettingsSaving(true);
    setError('');
    setNotice('');

    try {
      const payload = {
        site_id: Number(selectedSiteId),
        temperature_min: Number(settings.temperature_min),
        temperature_max: Number(settings.temperature_max),
        humidity_min: Number(settings.humidity_min),
        humidity_max: Number(settings.humidity_max),
      };

      const response = await api.post(
        '/api/member/smartfarm/environment-settings',
        payload
      );

      const data = response.data || {};
      if (!data.ok) {
        throw new Error(data.error || '환경 기준을 저장하지 못했습니다.');
      }

      setSettings({
        temperature_min: data.settings?.temperature_min ?? '',
        temperature_max: data.settings?.temperature_max ?? '',
        humidity_min: data.settings?.humidity_min ?? '',
        humidity_max: data.settings?.humidity_max ?? '',
      });
      setNotice(data.message || '환경 기준을 저장했습니다.');
      await loadAnalysis(selectedSiteId, period);
    } catch (err) {
      handleError(err, '환경 기준을 저장하지 못했습니다.');
    } finally {
      setSettingsSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div
        className="container-fluid py-5 farm-diary-page text-center"
        style={{ maxWidth: 1380 }}
      >
        <div className="spinner-border text-success" role="status" />
        <div className="mt-3 text-secondary">환경분석을 불러오고 있습니다.</div>
      </div>
    );
  }

  if (sites.length === 0) {
    return (
      <div
        className="container-fluid py-4 farm-diary-page"
        style={{ maxWidth: 1380 }}
      >
        <div className="card border-0 shadow-sm">
          <div className="card-body py-5 text-center">
            <div className="fs-1 mb-3">🌱</div>
            <h2 className="h4 fw-bold">등록된 스마트팜이 없습니다</h2>
            <p className="text-secondary mb-0">
              스마트팜 설치가 완료되면 환경분석을 이용할 수 있습니다.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const selectedSite =
    sites.find((site) => String(site.id) === String(selectedSiteId)) || null;

  return (
    <div className="container-fluid py-4 farm-diary-page" style={{ maxWidth: 1380 }}>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h2 className="fw-bold mb-1">환경분석</h2>
          <div className="text-secondary">
            농장의 온도와 습도 변화를 확인하고 정상 기준을 설정합니다.
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate('/smartfarm')}
        >
          대시보드
        </button>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-3 p-md-4">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-6">
              <label htmlFor="analysisSite" className="form-label fw-semibold">
                농장
              </label>
              <select
                id="analysisSite"
                className="form-select"
                value={selectedSiteId}
                onChange={(event) => setSelectedSiteId(event.target.value)}
              >
                {sites.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.site_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-12 col-md-6">
              <div className="form-label fw-semibold">조회 기간</div>
              <div className="btn-group w-100" role="group">
                {PERIOD_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className={`btn ${
                      period === option.value
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => setPeriod(option.value)}
                  >
                    {option.label}
                  </button> 
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <form
        className="card border-0 shadow-sm mb-4"
        onSubmit={saveSettings}
      >
        <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2 py-3">
          <div>
            <strong>환경 기준 설정</strong>
            <div className="small text-secondary mt-1">
              {selectedSite?.site_name || '선택한 농장'}에만 적용됩니다.
            </div>
          </div>
          <button
            type="submit"
            className="btn btn-success"
            disabled={settingsSaving}
          >
            {settingsSaving ? '저장 중...' : '기준 저장'}
          </button>
        </div>

        <div className="card-body p-3 p-md-4">
          <div className="row g-3">
            {[
              ['temperature_min', '최저 온도', '℃'],
              ['temperature_max', '최고 온도', '℃'],
              ['humidity_min', '최저 습도', '%'],
              ['humidity_max', '최고 습도', '%'],
            ].map(([name, label, unit]) => (
              <div className="col-6 col-lg-3" key={name}>
                <label htmlFor={name} className="form-label small">
                  {label}
                </label>
                <div className="input-group">
                  <input
                    id={name}
                    name={name}
                    type="number"
                    step="0.1"
                    className="form-control"
                    value={settings[name]}
                    onChange={handleSettingChange}
                    required
                  />
                  <span className="input-group-text">{unit}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </form>

      {analysisLoading ? (
        <div className="py-5 text-center">
          <div className="spinner-border text-success" role="status" />
          <div className="mt-3 text-secondary">HA 이력을 분석하고 있습니다.</div>
        </div>
      ) : (
        <>
          <MetricCard
            metricKey="temperature"
            metric={analysis?.metrics?.temperature}
            settings={settings}
            periodDays={Number(period)}
          />
          <MetricCard
            metricKey="humidity"
            metric={analysis?.metrics?.humidity}
            settings={settings}
            periodDays={Number(period)}
          />
        </>
      )}
    </div>
  );
};

export default MemberSmartfarmAnalysis;
